<template>
  <div class="flex h-full flex-col bg-zinc-900 text-zinc-100">
    <div class="border-b border-zinc-800 px-4 py-3">
      <div class="flex items-center justify-between">
        <h3 class="text-base font-bold">Skills</h3>
        <button
          class="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-500"
          @click="openGenerator()"
        >
          ✨ New Skill
        </button>
      </div>
      <p class="mt-1 text-xs text-zinc-500">
        AI tools with their own node types. Wire a skill node into AIToolbox →
        AIToolPlus.
      </p>
    </div>

    <div class="flex-1 overflow-y-auto p-3">
      <p v-if="loading" class="text-sm text-zinc-400">Loading…</p>
      <p v-else-if="!skills.length" class="text-sm text-zinc-500">
        No skills yet — create one with ✨ New Skill.
      </p>
      <div v-else class="flex flex-col gap-2">
        <div
          v-for="skill in skills"
          :key="skill.name"
          class="rounded-lg border border-zinc-800 bg-zinc-800/60 p-3"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="truncate text-sm font-semibold">{{ skill.name }}</p>
              <p class="mt-0.5 line-clamp-2 text-xs text-zinc-400">
                {{ skill.description }}
              </p>
            </div>
            <div class="flex shrink-0 gap-1">
              <button
                class="rounded p-1 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-100"
                title="Edit"
                @click="openGenerator(skill.name)"
              >
                ✏️
              </button>
              <button
                class="rounded p-1 text-red-400 hover:bg-zinc-700"
                title="Delete"
                @click="removeSkill(skill.name)"
              >
                🗑
              </button>
            </div>
          </div>
        </div>
      </div>
      <p v-if="error" class="mt-2 text-sm text-red-400">{{ error }}</p>
    </div>

    <SkillGeneratorDialog
      v-if="generatorOpen"
      :initial-name="editingName"
      @close="generatorOpen = false"
      @saved="loadSkills"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

import SkillGeneratorDialog from '@/components/skills/SkillGeneratorDialog.vue'
import { skillsApi } from '@/components/skills/skillsApi'
import type { SkillMeta } from '@/components/skills/skillsApi'

const skills = ref<SkillMeta[]>([])
const loading = ref(true)
const error = ref('')
const generatorOpen = ref(false)
const editingName = ref('')

onMounted(loadSkills)

async function loadSkills() {
  loading.value = true
  error.value = ''
  try {
    const res = await skillsApi.list()
    if (res.ok) skills.value = res.items ?? []
    else error.value = res.error ?? 'Failed to load skills'
  } finally {
    loading.value = false
  }
}

function openGenerator(name = '') {
  editingName.value = name
  generatorOpen.value = true
}

async function removeSkill(name: string) {
  if (!window.confirm(`Delete skill "${name}"? Its node type will disappear.`))
    return
  const res = await skillsApi.remove(name)
  if (!res.ok) {
    error.value = res.error ?? 'Delete failed'
    return
  }
  await skillsApi.reloadNodes()
  await loadSkills()
}
</script>
